import { Dialog } from "primereact/dialog";
import { Messages } from "primereact/messages";
import { ReactNode, useEffect, useRef, useState } from "react";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { FormFieldTypes } from "../app_utils/constants/FormFieldTypes";
import { getFormFieldComponent } from "../app_utils/components/FormFieldTemplates";
import { formatString } from "../app_utils/utils/Utils";
import { MISSING_FORM_INPUT_MESSAGE } from "../app_utils/constants/ErrorMessages";
import { CSS_COL_12, CSS_COL_4 } from "../app_utils/constants/Constants";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import * as labels from "../app_utils/constants/Labels";

interface ModalType {
  children?: ReactNode;
  messageRef?: any;
  record: any;
  reloadFn: any;
  isOpen: boolean;
  toggle: () => void;
}

interface RecordType {
  name: string;
}

interface ApiKeyResponse {
  data: {
    id: number;
    name: string;
    key: string;
    status: string;
    usageCount: number;
    lastUsedAt: string | null;
  };
}

const ApiKeyFormDialog = (props: ModalType) => {
  const [record, setRecord] = useState<RecordType | null>(props?.record);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const message = useRef<any>();

  useEffect(() => {
    setRecord(props?.record);
    setGeneratedKey(null);
  }, []);

  const clearForm = () => {
    setRecord(null);
    setGeneratedKey(null);
  };

  let userFormFields: any = [
    {
      type: FormFieldTypes.TEXT.toString(),
      label: "Name",
      value: record?.name,
      onChange: (e: any) => setRecord((prevState: any) => ({ ...prevState, name: e })),
      width: CSS_COL_12,
    },
  ];

  let userFields = userFormFields.map((userObjectField: any) => {
    return getFormFieldComponent(userObjectField);
  });

  const clearHints = () => {
    userFormFields.forEach((formField: any) => {
      if (formField.isValidHint) {
        formField.setHint(null);
      }
    });
  };

  const validateForm = () => {
    clearHints();
    let isFormValid: boolean = true;

    userFormFields.forEach((formField: any) => {
      if (
        formField.setHint &&
        (formField.value === null || formField.value === "" || formField.value === undefined)
      ) {
        isFormValid = false;
        formField.setHint(formatString(MISSING_FORM_INPUT_MESSAGE, formField.label));
      }
    });

    return isFormValid;
  };

  const saveUser = () => {
    if (validateForm()) {
      setIsSaving(true);
      new BaseApiServiceImpl("/api/v1/api-keys")
        .postRequestWithJsonResponse(record)
        .then(async (response: ApiKeyResponse) => {
          setIsSaving(false);
          setGeneratedKey(response.data.key);
          MessageUtils.showSuccessMessage(
            props?.messageRef,
            labels.LABEL_RECORD_SAVED_SUCCESSFULLY
          );
          props?.reloadFn();
        })
        .catch((error) => {
          setIsSaving(false);
          MessageUtils.showErrorMessage(message, error.message);
        });
    }
  };

  const closeDialog = () => {
    clearForm();
    props.toggle();
  };

  const copyKey = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey);
      MessageUtils.showSuccessMessage(message, "API key copied to clipboard");
    }
  };

  const userDetailsDialogFooter = (
    <>
      <Button
        label={labels.LABEL_CANCEL}
        icon={PrimeIcons.TIMES}
        className="p-button-text"
        onClick={closeDialog}
      />
      {!generatedKey && (
        <Button
          label={labels.LABEL_SAVE}
          icon={PrimeIcons.SAVE}
          className="p-button-secondary"
          onClick={saveUser}
          loading={isSaving}
        />
      )}
    </>
  );

  return (
    <Dialog
      visible={props.isOpen}
      header={"Generate Api Key"}
      footer={userDetailsDialogFooter}
      modal
      className="p-fluid"
      onHide={closeDialog}
      style={{ width: "50vw" }}
    >
      <div className="grid">
        <div className="col-12">
          <Messages ref={message} style={{ width: "100%" }} />
        </div>
        <div className="col-12">
          <p style={{ margin: "0 0 1rem", color: "#64748b" }}>
            The <strong>Name</strong> is for your own identification only. It helps you
            distinguish between multiple API keys (e.g., "Production App", "Staging
            Test"). The name is not sent to the server or included in API requests.
          </p>
        </div>
        {!generatedKey ? (
          userFields
        ) : (
          <div className="col-12">
            <div className="p-fluid">
              <label htmlFor="apiKey">Your API Key</label>
              <div className="p-inputgroup">
                <InputText
                  id="apiKey"
                  value={generatedKey}
                  readOnly
                  className="p-inputtext-sm"
                />
                <Button
                  icon={PrimeIcons.COPY}
                  className="p-button-outlined"
                  onClick={copyKey}
                  tooltip="Copy to clipboard"
                />
              </div>
              <small className="mt-2">
                Copy this key now. You will not be able to see it again.
              </small>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default ApiKeyFormDialog;
